// The API returns timestamps as "2026-09-18 06:53:02.0" - a space instead of
// "T" and no timezone marker. Two things go wrong if you hand that straight to
// new Date():
//
//  1. Only some engines accept the space form. Chrome does; Safari returns
//     Invalid Date, so anything on an iPad silently loses its timing.
//  2. With no zone marker the engine assumes local time. If the server is
//     sending UTC and the terminal is at, say, UTC+5:30, every timestamp reads
//     as five and a half hours old the moment it is written.
//
// So the components are parsed by hand, and the zone is worked out from the
// value itself rather than configured: a timestamp can never be in the future,
// so of the two readings we take the most recent one that isn't. Whichever way
// the server sends them, the wrong reading always lands either in the future
// (and is discarded) or further in the past (and loses).

const API_DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})?$/;

// Allows for a little clock drift between the server and the terminal.
const FUTURE_TOLERANCE_MS = 60 * 1000;

export const parseApiDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const match = API_DATE_RE.exec(raw);

  if (!match) {
    const direct = new Date(raw);
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  const [, year, month, day, hour, minute, second, fraction, zone] = match;
  const ms = fraction ? Number(fraction.padEnd(3, "0").slice(0, 3)) : 0;
  const parts = [
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    ms,
  ];

  // An explicit zone is unambiguous - respect it.
  if (zone) {
    const direct = new Date(raw.replace(" ", "T"));
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  const asUtc = new Date(Date.UTC(...parts));
  const asLocal = new Date(...parts);
  const limit = Date.now() + FUTURE_TOLERANCE_MS;

  const usable = [asUtc, asLocal].filter((date) => date.getTime() <= limit);

  // Both in the future means the clocks disagree badly; the earlier reading is
  // the least wrong.
  if (!usable.length) {
    return asUtc.getTime() < asLocal.getTime() ? asUtc : asLocal;
  }

  return usable.reduce((best, date) =>
    date.getTime() > best.getTime() ? date : best,
  );
};

// Milliseconds since the timestamp, never negative. 0 when unparseable, so a
// missing or malformed value never reads as "ages ago".
export const msSince = (value, now = Date.now()) => {
  const date = parseApiDate(value);

  if (!date) return 0;

  return Math.max(0, now - date.getTime());
};
